import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const MembershipContext = createContext(null);

const DEFAULT = {
  level: "explorer", levelLabel: "Kingdom Explorer", levelEmoji: "🏰", rank: 0, isGuest: true,
  nextLevel: "adventurer", nextLabel: "Kingdom Adventurer",
  counts: {}, items: {}, order: ["explorer", "adventurer", "royalty"],
  labels: { explorer: "Kingdom Explorer", adventurer: "Kingdom Adventurer", royalty: "Kingdom Royalty" },
  emoji: { explorer: "🏰", adventurer: "🛡️", royalty: "👑" }, maxChildren: 6,
};

export function MembershipProvider({ children }) {
  const [access, setAccess] = useState(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [upgrade, setUpgrade] = useState(null); // { requiredLevel, type }

  const refresh = useCallback(() => {
    setLoading(true);
    api.get("/content-access")
      .then(({ data }) => setAccess({ ...DEFAULT, ...data }))
      .catch(() => setAccess(DEFAULT))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const rankOf = useCallback((lvl) => access.order.indexOf(lvl || "explorer"), [access.order]);

  const requiredLevel = useCallback((type, itemId, index = 9999) => {
    const key = `${type}:${itemId}`;
    if (access.items && access.items[key]) return access.items[key];
    const c = (access.counts && access.counts[type]) || { explorer: 0, adventurer: 0 };
    if (index < (c.explorer || 0)) return "explorer";
    if (index < (c.adventurer || 0)) return "adventurer";
    return "royalty";
  }, [access]);

  const canAccess = useCallback((type, itemId, index = 9999) =>
    rankOf(access.level) >= rankOf(requiredLevel(type, itemId, index)),
  [access.level, rankOf, requiredLevel]);

  // Open the Kingdom upgrade modal for a required level.
  const promptUpgrade = useCallback((requiredLvl, type) => setUpgrade({ requiredLevel: requiredLvl, type }), []);
  const closeUpgrade = useCallback(() => setUpgrade(null), []);

  return (
    <MembershipContext.Provider value={{
      access, loading, refresh, canAccess, requiredLevel, rankOf,
      upgrade, promptUpgrade, closeUpgrade,
    }}>
      {children}
    </MembershipContext.Provider>
  );
}

export function useMembership() {
  const ctx = useContext(MembershipContext);
  if (!ctx) throw new Error("useMembership must be used within MembershipProvider");
  return ctx;
}
